import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, forkJoin, of, takeUntil } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { PlaylistViewState } from '../../../../../page-modules/playlist/store/view/playlist-view.state';
import { CORE_RESOURCE_DATA_SERVICE, ResourceDataService } from '../../../../../page-modules/resource/services/editor/core/data.service';
import { ResourceAdminState } from '../../../../../page-modules/resource/store/admin/resource-admin.state';
import { TEXT_FORM_MAX_WORDS_LIMIT } from '../../../../../shared/constants/constants';
import { canTriggerSearch } from '../../../../../shared/helpers/content-helper';
import { ConnectedTextBoxFormContent, Form } from '../../../../../shared/models';
import { AllContentPage, AllContentPlaylist } from '../../../../../shared/models/all-content/all-content.model';

@Component({
  selector: 'ptl-form-connected-textbox',
  templateUrl: './form-connected-textbox.component.html',
  styleUrls: ['../form.component.scss', './form-connected-textbox.component.scss'],
})
export class FormConnectedTextboxComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Receives the input data object */
  @Input() form: Form;
  @Input() canRemoveItem?: boolean;
  @Input() position?: number | string;
  @Input() enableFormsListRequests = false;

  /** Emits content data on saveForm() */
  @Output() formElementAdded: EventEmitter<Form> = new EventEmitter<Form>();

  /** Emits removing event of this form with index */
  @Output() formElementRemoved = new EventEmitter<void>();
  @Output() formElementUpdated = new EventEmitter<Form<ConnectedTextBoxFormContent>>();

  @ViewChild('formTitleInput') private formTitleInput: ElementRef<HTMLInputElement>;

  @Select(ResourceAdminState.contentStateChanged)
  contentStateChanged$: Observable<boolean>;

  @Select(PlaylistViewState.playlistId)
  private playlistId$: Observable<string>;

  expanded = true;
  formPlayLists: AllContentPlaylist[];
  formCardLists: AllContentPage[];
  formLists: { title: string; id: string }[];
  forceExpand = false;

  selectedPlaylistUid: string;
  selectedCardUid = '';
  selectedFormUid = '';
  focused = false;
  hasError = false;
  connectedTextboxForm: FormGroup;
  saveInProgress = false;
  searchTerm = '';
  cardTitle = '';
  formTitle = '';
  searchFormPlayListInProgress = false;
  selected = '-';

  private playlistId: string;
  private subscriptionEnd$ = new EventEmitter<void>();
  private searchInputSubject$ = new Subject<string>();

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    @Inject(CORE_RESOURCE_DATA_SERVICE) private resourceDataService: ResourceDataService,
  ) {
    this.connectedTextboxForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      wordLimit: [TEXT_FORM_MAX_WORDS_LIMIT, [Validators.required]],
      answerType: ['RICH', [Validators.required]],
      connection: this.formBuilder.group({
        playlistUid: [''],
        cardUid: ['', [Validators.required]],
        formUid: ['', [Validators.required]],
      }),
    });
  }

  ngOnInit(): void {
    this.handleFormContent();

    this.connectedTextboxForm.patchValue({ ...this.form?.content });

    this.playlistId$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((playlistId: string) => {
      if (playlistId) {
        this.playlistId = playlistId;
        this.setSelectedFields(this.playlistId);
      }
    });

    this.contentStateChanged$.pipe(takeUntil(this.subscriptionEnd$)).subscribe(() => {
      this.saveInProgress = false;
    });

    this.searchInputSubject$
      .pipe(debounceTime(300), takeUntil(this.subscriptionEnd$))
      .subscribe((searchValue: string) => this.getPlayList(searchValue));
    this.forceExpand = this.form?.content?.forceExpand;
  }

  ngAfterViewInit(): void {
    const titleInput = this.formTitleInput?.nativeElement;
    if (this.forceExpand && titleInput) {
      setTimeout(() => {
        const titleLength = titleInput.value.length;
        titleInput.focus();
        titleInput.setSelectionRange(titleLength, titleLength);
      });
    }
  }

  saveForm(): void {
    this.hasError = false;
    if (this.isTextBoxIsValid()) {
      this.expanded = false;
      this.forceExpand = false;
      this.emitFormUpdate();
      if (!this.form?.uid) {
        this.outputData();
      } else {
        this.formElementAdded.emit(this.form);
      }
      this.saveInProgress = false;
    } else {
      this.hasError = true;
      this.saveInProgress = false;
    }
  }

  expandForm(): void {
    this.expanded = true;
  }

  collapseForm(event: PointerEvent): void {
    event.stopPropagation();
    this.expanded = false;
    this.forceExpand = false;
  }

  outputData(): void {
    const outputData: Form<ConnectedTextBoxFormContent> = {
      ...this.form,
      newAddedForm: false,
      content: {
        ...this.buildContent(),
        forceExpand: false,
        submitted: (this.form.content as ConnectedTextBoxFormContent)?.submitted ?? false,
        formIsActive: (this.form.content as ConnectedTextBoxFormContent)?.formIsActive ?? false,
      },
    };

    this.formElementAdded.emit(outputData);
    this.updateConnectedTextboxForm(this.searchTerm, this.selectedCardUid, this.selectedFormUid);
  }

  removeForm(): void {
    this.formElementRemoved.emit();
  }

  onTextPaste(event: ClipboardEvent): void {
    if (event.type === 'paste') {
      setTimeout(() => {
        this.searchTerm = (event.target as HTMLInputElement).value.trim();
        this.searchInputSubject$.next(this.searchTerm);
      }, 0);
    }
  }

  onSearchInputChange(event: KeyboardEvent): void {
    this.playlistId = this.playlistId ?? this.store.selectSnapshot(PlaylistViewState.playlistId);
    if (canTriggerSearch(event)) {
      this.searchFormPlayListInProgress = true;
      const searchTerm = (event.target as HTMLInputElement).value.trim();

      if (searchTerm) {
        this.searchInputSubject$.next(searchTerm);
      }
    }
  }

  getSelectedPlaylistUid(event: MatAutocompleteSelectedEvent): void {
    this.selectedPlaylistUid = event.option.id;
    this.searchTerm = event.option.value;
    this.resetCardsAndFormsFields();
    this.getFormCards(this.selectedPlaylistUid);
  }

  getSelectedCardUid(event: MatSelectChange): void {
    this.selectedCardUid = event.value;
    // getForms calls getFormsList which makes the API request to /playlists/{playlistId}/forms-list
    // This happens both in old editor and Tiptap (when enableFormsListRequests is true)
    this.getForms(this.selectedCardUid);
  }

  getSelectedFormUid(event: MatSelectChange): void {
    this.selectedFormUid = event.value;
  }

  getFormCards(selectedPlaylistUid: string): void {
    this.resetSelectedValues(true);

    this.resourceDataService
      .getFormCardsByPlayListUid(this.playlistId, selectedPlaylistUid)
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          this.formCardLists = value || [];

          if (value.length === 1) {
            this.selectedCardUid = value[0].id;
            this.getForms(this.selectedCardUid);
          } else if (this.enableFormsListRequests && this.selectedCardUid) {
            // If forms-list requests are enabled and card is already selected, make the request
            // This ensures the request is made when playlist is selected and card was already chosen
            this.getForms(this.selectedCardUid);
          }
        }
      });
  }

  getForms(selectedCardUid: string): void {
    this.resetSelectedValues();

    this.resourceDataService
      .getFormsList(this.playlistId, this.selectedPlaylistUid, selectedCardUid)
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          this.formLists = value || [];

          if (value.length === 1) {
            this.selectedFormUid = value[0].id;
          }
        }
      });
  }

  getPlayList(searchTerm: string): void {
    this.resourceDataService
      .getFormPlayListByUid(this.playlistId, searchTerm, 0, 20)
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(({ isSuccess, value }) => {
        this.searchFormPlayListInProgress = false;
        if (isSuccess) {
          this.formPlayLists = value?.content || [];
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$?.emit();
  }

  setSelectedFields(playlistId: string): void {
    const connectionForm = this.connectedTextboxForm.get('connection');
    this.selectedCardUid = connectionForm.get('cardUid').value;
    this.selectedPlaylistUid = connectionForm.get('playlistUid').value;
    this.selectedFormUid = connectionForm.get('formUid').value;

    if (!this.selectedPlaylistUid) {
      return;
    }

    forkJoin({
      formCards: this.resourceDataService.getFormCardsByPlayListUid(playlistId, this.selectedPlaylistUid),
      formsList: this.resourceDataService.getFormsList(this.playlistId, this.selectedPlaylistUid, this.selectedCardUid),
      formTitles: this.resourceDataService.getFormTitlesByPlayListUid(this.selectedPlaylistUid),
    })
      .pipe(
        takeUntil(this.subscriptionEnd$),
        switchMap(({ formCards, formsList, formTitles }) => {
          if (formCards.isSuccess) {
            this.formCardLists = formCards.value || [];
          }

          if (formsList.isSuccess) {
            this.formLists = formsList.value || [];
          }

          if (formTitles.isSuccess) {
            this.searchTerm = formTitles.value.title;
          }

          return of(null);
        }),
      )
      .subscribe();
  }

  private resetSelectedValues(resetCard = false): void {
    if (resetCard) {
      this.selectedCardUid = null;
    }

    this.selectedFormUid = null;
    this.formLists = null;
  }

  private handleFormContent(): void {
    const content = this.form ? (this.form.content as ConnectedTextBoxFormContent) : null;

    if (this.form && (this.form.uid || content?.title)) {
      this.setFormContent(content, this.form.newAddedForm);
    }
  }

  private updateConnectedTextboxForm(searchTerm: string, selectedCardUid: string, selectedFormUid: string): void {
    this.connectedTextboxForm.get('connection.playlistUid').setValue(this.selectedPlaylistUid || searchTerm);
    this.connectedTextboxForm.get('connection.cardUid').setValue(selectedCardUid);
    this.connectedTextboxForm.get('connection.formUid').setValue(selectedFormUid);
  }

  private resetCardsAndFormsFields(): void {
    this.connectedTextboxForm.get('connection.cardUid').reset();
    this.connectedTextboxForm.get('connection.formUid').reset();
  }

  private setFormContent(content: ConnectedTextBoxFormContent, newAddedForm: boolean): void {
    this.expanded = newAddedForm ?? false;
    this.connectedTextboxForm.setValue({
      title: content.title,
      wordLimit: content.wordLimit,
      answerType: content.answerType ?? 'RICH',
      connection: {
        playlistUid: content.connection?.playlistUid ?? '',
        cardUid: content.connection?.cardUid ?? '',
        formUid: content.connection?.formUid ?? '',
      },
    });
  }

  private isTextBoxIsValid(): boolean {
    return this.selectedPlaylistUid && this.connectedTextboxForm?.valid;
  }

  private buildContent(): ConnectedTextBoxFormContent {
    const existingContent = (this.form?.content as ConnectedTextBoxFormContent) ?? ({} as ConnectedTextBoxFormContent);
    const value = this.connectedTextboxForm.getRawValue();
    return {
      ...existingContent,
      ...value,
      type: 'CONNECTED_TEXTBOX',
      title: value.title ?? existingContent.title ?? 'Connected Text Box',
      wordLimit: Number(value.wordLimit) || TEXT_FORM_MAX_WORDS_LIMIT,
      answerType: value.answerType ?? existingContent.answerType ?? 'RICH',
      connection: {
        playlistUid: this.selectedPlaylistUid ?? value.connection?.playlistUid ?? '',
        cardUid: this.selectedCardUid || (value.connection?.cardUid ?? ''),
        formUid: this.selectedFormUid || (value.connection?.formUid ?? ''),
      },
      minWordsLimit: existingContent.minWordsLimit ?? undefined,
      aiAssistantEnabled: existingContent.aiAssistantEnabled ?? false,
      aiAssistantSettingsUid: existingContent.aiAssistantSettingsUid ?? null,
      isTemplate: existingContent.isTemplate ?? false,
      submitted: existingContent.submitted ?? false,
      formIsActive: existingContent.formIsActive ?? false,
      forceExpand: this.forceExpand,
    };
  }

  private emitFormUpdate(): void {
    if (!this.form || !this.connectedTextboxForm) {
      return;
    }

    const content = this.buildContent();
    const payload: Form<ConnectedTextBoxFormContent> = {
      ...(this.form as Form<ConnectedTextBoxFormContent>),
      newAddedForm: this.form?.uid ? false : (this.form?.newAddedForm ?? false),
      content,
    };

    this.form = payload as Form;
    this.formElementUpdated.emit(payload);
  }
}
